import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const linkClasses = scrolled
    ? 'text-foreground-700 hover:text-foreground-950'
    : 'text-white/85 hover:text-white';

  const logoPrimary = scrolled ? 'text-primary-500' : 'text-primary-300';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-sm border-b border-background-200/60'
          : 'bg-black/20 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group"
          >
            <span className={`font-heading text-xl md:text-2xl font-bold tracking-tight transition-colors duration-300 ${scrolled ? 'text-foreground-950' : 'text-white'}`}>
              PandaBuy<span className={logoPrimary}>Spreadsheet</span>
            </span>
          </Link>

          {/* Center Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${linkClasses}`}
            >
              Home
            </Link>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${linkClasses}`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-background-50 rounded-lg border border-background-200/70 py-2 shadow-lg z-50">
                  <div className="max-h-80 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setDropdownOpen(false)}
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/blog/pandabuy-spreadsheet-comparison-guide"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${linkClasses}`}
            >
              Shopping Guide
            </Link>

            <Link
              to="/blog/pandabuy-spreadsheet-comparison-guide"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${linkClasses}`}
            >
              Blog
            </Link>
          </div>

          {/* Right CTAs */}
          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-secondary-200 text-foreground-900 text-sm font-medium hover:bg-secondary-300 transition-colors whitespace-nowrap cursor-pointer"
            >
              Pandabuy Sheet
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>

            {/* Mobile menu toggle */}
            <button
              className={`md:hidden flex items-center justify-center w-10 h-10 rounded-lg transition-colors cursor-pointer ${scrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-white hover:bg-white/10'}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <div className="md:hidden pb-5 border-t border-white/15">
            <div className="flex flex-col gap-0.5 pt-3">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
              >
                <i className="ri-home-4-line text-base w-5 h-5 flex items-center justify-center"></i>
                Home
              </Link>
              <Link
                to="/blog/pandabuy-spreadsheet-comparison-guide"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
              >
                <i className="ri-book-open-line text-base w-5 h-5 flex items-center justify-center"></i>
                Shopping Guide
              </Link>
              <Link
                to="/blog/pandabuy-spreadsheet-comparison-guide"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
              >
                <i className="ri-article-line text-base w-5 h-5 flex items-center justify-center"></i>
                Blog
              </Link>
              <div className="border-t border-background-200/60 my-2"></div>
              <div className="px-3 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                Browse Categories
              </div>
              <div className="grid grid-cols-2 gap-0.5 px-2">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="px-2 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors truncate"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}